<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import { avatarAdd, avatarPut, avatarDel } from "@/common/api/rest/avatar";
import { consoleGet, consolePatch } from "@/common/api/rest/console/console";

import type { ConsoleType } from "@/common/types/console/console";
import type { TranslationFieldsType } from "@/common/types/translation";

import { FormConsole, ListTranslate } from "@/components";

import {
  baseAvatar,
  baseConfirm,
  breadcrumbSimple,
  iconMenu,
  svgIcon,
} from "@/common/components";

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();

const emit = defineEmits(["update:data"]);

const consoleData = ref<ConsoleType | null>(null);

const showAvatarDel = ref(false);

const translationFields = ref<TranslationFieldsType[]>([
  {
    type: "input",
    field: "name",
    label: "common.labels.name",
    value: "",
  },
  {
    type: "textarea",
    field: "description",
    label: "common.labels.description",
    value: "",
  },
]);

const consoleId = computed(() => route.params.item as string);

const breadcrumbList = computed(() => [
  {
    key: "collections",
    label: t("common.labels.collections"),
    url: `/collection`,
  },
  {
    key: "collection",
    label: t("collection.labels.consoles"),
    url: `/collection/console`,
  },
  {
    key: "edit",
    label: consoleData.value?.name ?? "...",
  },
]);

const menuList = computed(() => [
  {
    key: "infos",
    icon: "field",
    label: t("collection.labels.infos"),
    url: `/collection/console/${consoleId.value}/infos`,
  },
  {
    key: "gallery",
    icon: "no-image",
    label: t("collection.labels.images"),
    url: `/collection/console/${consoleId.value}/gallery`,
  },
  // {
  //   key: "slug",
  //   icon: "no-image",
  //   label: t("collection.labels.slugs"),
  //   url: `/collection/console/${consoleId.value}/slug`,
  // },
  {
    key: "details",
    icon: "no-image",
    label: t("collection.labels.details"),
    url: `/collection/console/${consoleId.value}/details`,
  },
]);

async function fetchData() {
  await consoleGet(consoleId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        consoleData.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function patchData(payload: Partial<ConsoleType>) {
  consolePatch(consoleId.value, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        // emit("update:data");
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

const type = computed(() => route.params.type as string);

// AVATAR
function addAvatar(avatar: string) {
  if (!store.getters.user) return;

  store.commit("SET_LOADING", true);

  avatarAdd(consoleId.value, avatar)
    .then((response) => {
      const { success, message } = response;

      toast({ message: message, type: success ? "success" : "error" });

      emit("update:data");
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function updateAvatar(avatar: string) {
  if (!consoleData.value.avatar) return;

  store.commit("SET_LOADING", true);

  avatarPut(consoleId.value, avatar)
    .then((response) => {
      const { success, message } = response;

      toast({ message: message, type: success ? "success" : "error" });

      emit("update:data");
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function changeAvatar(avatar: string) {
  if (consoleData.value.avatar) {
    updateAvatar(avatar);
  } else {
    addAvatar(avatar);
  }
}

function delAvatar() {
  showAvatarDel.value = true;
}

function delAvatarConfirm() {
  if (!consoleData.value?.avatar) return;

  store.commit("SET_LOADING", true);

  avatarDel(consoleId.value)
    .then((response) => {
      const { success, message } = response;

      toast({ message: message, type: success ? "success" : "error" });

      emit("update:data");
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      showAvatarDel.value = false;
      store.commit("SET_LOADING", false);
    });
}

async function changeMenu(url: string) {
  router.push(url);
}

function back() {
  router.push(`/collection/console`);
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <article class="register-page">
    <section class="page-header">
      <breadcrumb-simple
        :items="breadcrumbList"
        :show-home="true"
        url-home="/home"
        active="edit"
      />
    </section>

    <section class="page-content">
      <div class="tab-header">
        <div class="title">
          <svg-icon icon="gamepad" size="2rem" />
          {{ t("collection.labels.edit_item") }}
        </div>

        <div class="actions">
          <button class="btn btn-default w-8 h-8" @click="back()">
            <svg-icon icon="close" size="1rem" />
          </button>
        </div>
      </div>

      <div class="tab-content">
        <icon-menu
          :tabs="menuList"
          active="infos"
          size="3rem"
          @selected="changeMenu($event)"
        />

        <template v-if="consoleData">
          <div class="flex justify-center">
            <base-avatar
              position="center"
              size="20rem"
              :is-edit="true"
              :show-image="true"
              :url="consoleData?.avatar?.large"
              :aspect-ratio="1"
              border-radius="0.25rem"
              @change="changeAvatar($event)"
              @del="delAvatar()"
            />
          </div>

          <form-console
            :data="consoleData"
            :type="type"
            :is-patch="true"
            @update:data="patchData($event)"
          />

          <list-translate
            :owner-id="consoleId"
            :language="locale.toLowerCase()"
            :fields="translationFields"
          />

          <base-confirm
            :show="showAvatarDel"
            :title="t('common.modals.titles.remove_image').toUpperCase()"
            :message="t('common.modals.messages.remove_image')"
            @cancel="showAvatarDel = false"
            @confirm="delAvatarConfirm()"
          />
        </template>
      </div>
    </section>
  </article>
</template>

<style lang="scss" scoped>
.register-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .page-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: inherit;
    background: white;
    border-radius: 0.5rem;
    padding-inline: 1rem;
    padding-block: 0.5rem;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0.5rem;

    .tab-header {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding-inline: 1.25rem;
      padding-block: 0.75rem;
      margin-block-end: 1.5rem;
      border-block-end: 1px solid var(--border);

      .title {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
        font-size: 1.2rem;
        text-transform: uppercase;
      }

      .actions {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
        gap: 0.75rem;

        button {
          width: 1.75rem;
          height: 1.75rem;
          padding: 0;
        }
      }
    }

    .tab-content {
      display: flex;
      flex-direction: column;
      gap: 1.54rem;
      padding-inline: 1.25rem;
      padding-block-end: 1.25rem;
    }
  }
}
</style>
